/**
 * The two Finder views that aren't a grid or a table.
 *
 *   ColumnsView — Miller columns: one column per folder from Documents down
 *                 to the window's folder, the path highlighted; choosing a
 *                 folder opens its column to the right, choosing a file shows
 *                 its preview in a last column.
 *   GalleryView — a large preview of the chosen item above a filmstrip of
 *                 everything in the folder.
 *
 * Both use the same shared machinery as the grid: selection, drag and drop
 * (folder rows and column backgrounds are drop targets), the context menu,
 * and open-the-preferred-way.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatBytes, formatModified, typeLabel } from '@/components/files/file-dialog/format';
import { isImageMime } from '../../lib/file-display';
import { useFileOpeners } from '../../hooks/useFileOpeners';
import { ROOT_CONTAINER, type FolderView } from '../../lib/desktop-layout';
import { useDesktop } from './desktop-context';
import { useContainerItems } from './useContainerItems';
import type { DesktopItem } from './desktop-model';
import { ItemGlyph } from './DesktopIcon';
import { DocumentThumbnail } from '../shared/DocumentThumbnail';
import { FilePreviewPane } from '../shared/FilePreviewPane';

const DRAG_THRESHOLD = 4;

/** Tell the Desktop what a view shows (for the status bar, Select All, paste). */
function useRegisterItems(
  windowId: string,
  container: string,
  data: ReturnType<typeof useContainerItems>,
) {
  const { registerCanvas } = useDesktop();
  const { items, loadedFiles, totalFiles } = data;
  useEffect(() => {
    registerCanvas(windowId, {
      container,
      positions: new Map(),
      columns: 1,
      items,
      loadedFiles,
      totalFiles,
      free: false,
    });
  }, [registerCanvas, windowId, container, items, loadedFiles, totalFiles]);
  useEffect(() => () => registerCanvas(windowId, null), [registerCanvas, windowId]);
}

/** Only the last column registers — it is the window's folder. */
function RegisterItems({
  windowId,
  container,
  data,
}: {
  windowId: string;
  container: string;
  data: ReturnType<typeof useContainerItems>;
}) {
  useRegisterItems(windowId, container, data);
  return null;
}

/** Pointer handling for a row or tile: click selects, a small move drags. */
function usePressToDrag(
  container: string,
  item: DesktopItem,
  onClick: (e: React.MouseEvent) => void,
) {
  const d = useDesktop();
  return {
    onPointerDown: (e: React.PointerEvent<HTMLElement>) => {
      if (e.button !== 0 || e.pointerType === 'touch') return;
      e.stopPropagation();
      const rect = e.currentTarget.getBoundingClientRect();
      const start = { x: e.clientX, y: e.clientY };
      const grab = { dx: e.clientX - rect.left, dy: e.clientY - rect.top };
      const move = (ev: PointerEvent) => {
        if (Math.hypot(ev.clientX - start.x, ev.clientY - start.y) < DRAG_THRESHOLD) return;
        cleanup();
        d.select(container, [item.key], 'replace');
        d.drag.begin({
          container,
          items: [item],
          offsets: new Map([[item.key, { dc: 0, dr: 0 }]]),
          grab,
          pointer: { x: ev.clientX, y: ev.clientY },
        });
      };
      const cleanup = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', cleanup);
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', cleanup);
    },
    onClick,
    onDragStart: (e: React.DragEvent) => e.preventDefault(),
  };
}

// ---------------------------------------------------------------------------
// Columns
// ---------------------------------------------------------------------------

export function ColumnsView({
  container,
  view,
  windowId,
  onNavigate,
}: {
  /** The window's folder (the last folder column). */
  container: string;
  view: FolderView;
  windowId: string;
  onNavigate: (container: string) => void;
}) {
  const d = useDesktop();
  const scrollRef = useRef<HTMLDivElement>(null);
  // Documents → … → the window's folder.
  const chain = useMemo(() => {
    const ids: string[] = [];
    let cursor = container === ROOT_CONTAINER ? undefined : d.foldersById.get(container);
    for (let guard = 0; cursor && guard < 64; guard += 1) {
      ids.unshift(cursor.folderId);
      cursor = cursor.parentFolderId ? d.foldersById.get(cursor.parentFolderId) : undefined;
    }
    return [ROOT_CONTAINER, ...ids];
  }, [container, d.foldersById]);

  const selectedFile =
    d.selection.container === container && d.selection.keys.size === 1
      ? [...d.selection.keys][0]
      : null;

  useEffect(() => {
    // Keep the newest column in view.
    const el = scrollRef.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [chain.length, selectedFile]);

  return (
    <div ref={scrollRef} className="flex h-full overflow-x-auto overflow-y-hidden">
      {chain.map((col, i) => (
        <Column
          key={col}
          container={col}
          view={view}
          windowId={windowId}
          openChild={chain[i + 1] ?? null}
          isLast={i === chain.length - 1}
          onNavigate={onNavigate}
        />
      ))}
      {selectedFile && <PreviewColumn container={container} documentKey={selectedFile} />}
    </div>
  );
}

function Column({
  container,
  view,
  windowId,
  openChild,
  isLast,
  onNavigate,
}: {
  container: string;
  view: FolderView;
  windowId: string;
  /** The folder open to the right of this column. */
  openChild: string | null;
  isLast: boolean;
  onNavigate: (container: string) => void;
}) {
  const d = useDesktop();
  const data = useContainerItems({
    holderId: d.holderId,
    container,
    folders: d.folders,
    trashFull: d.trashFull,
    includeTrash: false,
    view,
  });
  const selected = d.selection.container === container ? d.selection.keys : null;
  const listRef = useRef<HTMLDivElement>(null);

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const items = data.items;
    const current =
      items.findIndex((i) => selected?.has(i.key)) >= 0
        ? items.findIndex((i) => selected?.has(i.key))
        : items.findIndex((i) => i.key === openChild);
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const next =
        items[Math.max(0, Math.min(items.length - 1, current + (e.key === 'ArrowDown' ? 1 : -1)))];
      if (!next) return;
      if (next.kind === 'folder') onNavigate(next.key);
      else {
        if (!isLast) onNavigate(container);
        d.select(container, [next.key], 'replace');
      }
    } else if (e.key === 'ArrowRight' && current >= 0 && items[current].kind === 'folder') {
      e.preventDefault();
      onNavigate(items[current].key);
    } else if (e.key === 'ArrowLeft' && container !== ROOT_CONTAINER) {
      e.preventDefault();
      onNavigate(d.foldersById.get(container)?.parentFolderId ?? ROOT_CONTAINER);
    } else if (e.metaKey && (e.key === 'o' || e.key === 'O') && current >= 0) {
      e.preventDefault();
      d.openItem(items[current], container, { windowId });
    }
  };

  return (
    <div
      ref={listRef}
      role="listbox"
      aria-label={
        container === ROOT_CONTAINER
          ? 'Documents'
          : (d.foldersById.get(container)?.name ?? 'Folder')
      }
      tabIndex={0}
      onKeyDown={onKeyDown}
      data-desktop-drop={`container:${container}`}
      data-free="false"
      onContextMenu={(e) => {
        if (e.target !== e.currentTarget) return;
        e.preventDefault();
        d.openMenu({
          x: e.clientX,
          y: e.clientY,
          container,
          windowId,
          returnFocus: listRef.current,
        });
      }}
      className="h-full w-56 shrink-0 overflow-y-auto border-r border-black/10 py-1 outline-none focus-visible:bg-muted/20 dark:border-white/10"
    >
      {isLast && <RegisterItems windowId={windowId} container={container} data={data} />}
      {data.items.map((item) => (
        <ColumnRow
          key={item.key}
          item={item}
          container={container}
          windowId={windowId}
          onPath={item.key === openChild}
          selected={!!selected?.has(item.key)}
          onNavigate={onNavigate}
          isLast={isLast}
        />
      ))}
      {!data.loading && data.items.length === 0 && (
        <p className="px-3 py-2 text-xs text-muted-foreground">Empty</p>
      )}
      {data.hasMore && (
        <button
          type="button"
          onClick={data.loadMore}
          className="w-full px-3 py-1 text-left text-xs text-muted-foreground hover:underline"
        >
          {data.loadingMore ? 'Loading…' : 'Show more'}
        </button>
      )}
    </div>
  );
}

function ColumnRow({
  item,
  container,
  windowId,
  onPath,
  selected,
  isLast,
  onNavigate,
}: {
  item: DesktopItem;
  container: string;
  windowId: string;
  onPath: boolean;
  selected: boolean;
  isLast: boolean;
  onNavigate: (container: string) => void;
}) {
  const d = useDesktop();
  const press = usePressToDrag(container, item, (e) => {
    e.stopPropagation();
    if (item.kind === 'folder') {
      onNavigate(item.key);
      return;
    }
    // A file in an earlier column: the window moves to that folder so the
    // file's preview appears beside it.
    if (!isLast) onNavigate(container);
    d.select(container, [item.key], e.metaKey ? 'toggle' : 'replace');
  });
  const highlighted = selected || onPath;
  return (
    <div
      role="option"
      aria-selected={highlighted}
      data-desktop-key={item.key}
      data-desktop-container={container}
      {...(item.kind === 'folder' ? { 'data-desktop-drop': `folder:${item.key}` } : {})}
      {...press}
      onDoubleClick={(e) => {
        e.stopPropagation();
        d.openItem(item, container, { windowId, newWindow: e.metaKey });
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        d.select(container, [item.key], 'replace');
        d.openMenu({
          x: e.clientX,
          y: e.clientY,
          container,
          windowId,
          item,
          returnFocus: e.currentTarget,
        });
      }}
      className={cn(
        'mx-1 flex select-none items-center gap-2 rounded-md px-2 py-0.5 text-[13px]',
        highlighted
          ? selected || isLast
            ? 'bg-[#0a64d6] text-white'
            : 'bg-black/10 dark:bg-white/15'
          : 'hover:bg-black/5 dark:hover:bg-white/10',
        d.pending.has(item.key) && 'opacity-60',
      )}
    >
      <ItemGlyph item={item} size={16} />
      <span className="min-w-0 flex-1 truncate">{item.name}</span>
      {item.kind === 'folder' && (
        <ChevronRight className="h-3.5 w-3.5 shrink-0 opacity-60" aria-hidden />
      )}
    </div>
  );
}

function PreviewColumn({ container, documentKey }: { container: string; documentKey: string }) {
  const d = useDesktop();
  const { openerFor } = useFileOpeners();
  const data = useContainerItems({
    holderId: d.holderId,
    container,
    folders: d.folders,
    trashFull: d.trashFull,
    includeTrash: false,
    view: { mode: 'details', sort: 'name', dir: 'asc' },
  });
  const item = data.items.find((i) => i.key === documentKey);
  if (!item || item.kind !== 'file') return null;
  const opener = openerFor(item.doc);
  return (
    <div className="flex h-full w-72 shrink-0 flex-col gap-3 overflow-y-auto p-3">
      <div className="h-44 overflow-hidden rounded-md border border-black/10 dark:border-white/10">
        <FilePreviewPane
          doc={item.doc}
          preview={opener.preview === 'document' ? null : opener.preview}
          compact
        />
      </div>
      <div className="space-y-1 text-xs">
        <p className="break-words text-[13px] font-semibold">{item.name}</p>
        <p className="text-muted-foreground">
          {typeLabel(item.name, item.doc.mimeType)} — {formatBytes(item.doc.sizeBytes)}
        </p>
        <p className="text-muted-foreground">Modified {formatModified(item.doc.updatedAt)}</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Gallery
// ---------------------------------------------------------------------------

export function GalleryView({
  container,
  view,
  windowId,
}: {
  container: string;
  view: FolderView;
  windowId: string;
}) {
  const d = useDesktop();
  const { openerFor } = useFileOpeners();
  const data = useContainerItems({
    holderId: d.holderId,
    container,
    folders: d.folders,
    trashFull: d.trashFull,
    includeTrash: false,
    view,
  });
  useRegisterItems(windowId, container, data);
  const { items } = data;
  const selectedKey = d.selection.container === container ? [...d.selection.keys][0] : undefined;
  const [fallback, setFallback] = useState(0);
  const index = Math.max(
    0,
    selectedKey
      ? items.findIndex((i) => i.key === selectedKey)
      : Math.min(fallback, items.length - 1),
  );
  const current = items[index];
  const stripRef = useRef<HTMLDivElement>(null);

  const choose = (i: number) => {
    const target = items[i];
    if (!target) return;
    setFallback(i);
    d.select(container, [target.key], 'replace');
    stripRef.current
      ?.querySelector<HTMLElement>(`[data-gallery-index="${i}"]`)
      ?.scrollIntoView({ block: 'nearest', inline: 'center' });
  };

  const opener = current?.kind === 'file' ? openerFor(current.doc) : null;
  return (
    <div
      className="flex h-full flex-col outline-none"
      tabIndex={0}
      aria-label="Gallery"
      onKeyDown={(e) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
          e.preventDefault();
          choose(index + (e.key === 'ArrowRight' ? 1 : -1));
        } else if (
          current &&
          e.metaKey &&
          (e.key === 'o' || e.key === 'O' || e.key === 'ArrowDown')
        ) {
          e.preventDefault();
          d.openItem(current, container, { windowId });
        } else if (current && e.key === ' ') {
          e.preventDefault();
          d.quickLook(current);
        }
      }}
    >
      <div className="relative min-h-0 flex-1 bg-muted/30">
        {current?.kind === 'file' ? (
          <FilePreviewPane doc={current.doc} preview={opener?.preview ?? null} />
        ) : current ? (
          <div className="flex h-full flex-col items-center justify-center gap-3">
            <ItemGlyph item={current} size={128} />
            <p className="text-sm font-medium">{current.name}</p>
          </div>
        ) : (
          <p className="flex h-full items-center justify-center text-sm text-muted-foreground">
            {data.loading ? 'Loading…' : 'This folder is empty.'}
          </p>
        )}
      </div>
      <div
        ref={stripRef}
        role="listbox"
        aria-label="Items"
        data-desktop-drop={`container:${container}`}
        data-free="false"
        className="flex h-24 shrink-0 items-center gap-2 overflow-x-auto border-t border-black/10 px-3 dark:border-white/10"
      >
        {items.map((item, i) => (
          <GalleryTile
            key={item.key}
            item={item}
            index={i}
            container={container}
            windowId={windowId}
            selected={i === index}
            onChoose={() => choose(i)}
          />
        ))}
        {data.hasMore && (
          <button
            type="button"
            onClick={data.loadMore}
            className="shrink-0 text-xs text-muted-foreground hover:underline"
          >
            More…
          </button>
        )}
      </div>
    </div>
  );
}

function GalleryTile({
  item,
  index,
  container,
  windowId,
  selected,
  onChoose,
}: {
  item: DesktopItem;
  index: number;
  container: string;
  windowId: string;
  selected: boolean;
  onChoose: () => void;
}) {
  const d = useDesktop();
  const press = usePressToDrag(container, item, (e) => {
    e.stopPropagation();
    onChoose();
  });
  return (
    <div
      role="option"
      aria-selected={selected}
      aria-label={item.name}
      title={item.name}
      data-gallery-index={index}
      data-desktop-key={item.key}
      data-desktop-container={container}
      {...(item.kind === 'folder' ? { 'data-desktop-drop': `folder:${item.key}` } : {})}
      {...press}
      onDoubleClick={(e) => {
        e.stopPropagation();
        d.openItem(item, container, { windowId, newWindow: e.metaKey });
      }}
      onContextMenu={(e) => {
        e.preventDefault();
        d.select(container, [item.key], 'replace');
        d.openMenu({
          x: e.clientX,
          y: e.clientY,
          container,
          windowId,
          item,
          returnFocus: e.currentTarget,
        });
      }}
      className={cn(
        'flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-md',
        selected ? 'ring-2 ring-[#0a64d6]' : 'hover:bg-black/5 dark:hover:bg-white/10',
      )}
    >
      {item.kind === 'file' && isImageMime(item.doc.mimeType) ? (
        <DocumentThumbnail
          documentId={item.doc.documentId}
          name={item.name}
          iconClassName="h-8 w-8"
        />
      ) : (
        <ItemGlyph item={item} size={40} />
      )}
    </div>
  );
}
